/**
 * Platform Module
 *
 * Handles platform token acquisition and caching.
 *
 * Three modes:
 *  1. PKCE flow     → POST /sdk/authorize + /sdk/token (web + mobile, zero credentials)
 *  2. clientId      → POST /sdk/init (legacy browser-safe, origin-validated)
 *  3. platformToken → uses the pre-supplied token directly
 */

import type { PlatformTokenData } from '../types';
import { storage, STORAGE_KEYS } from '../utils/storage';
import { TryStyleApiError } from '../utils/fetcher';
import { generatePKCEPair } from '../utils/pkce';
import { detectPlatform, validatePlatformIdentifier, type PlatformInfo } from '../utils/platform';

interface PlatformModuleOptions {
  apiUrl: string;
  clientId?: string;
  initialPlatformToken?: string;
  usePKCE?: boolean;
  platformType?: 'web' | 'ios' | 'android';
  platformIdentifier?: string;
}

interface SdkConfigQueryOptions {
  productId?: string | string[];
  origin?: string;
  forceRefresh?: boolean;
}

interface SdkConfigEntry {
  value: any;
  expiresAt: number;
}

// Config is admin-managed — short TTL so retailer/firebase changes propagate quickly
const SDK_CONFIG_TTL_MS = 5 * 60 * 1000;

export class PlatformModule {
  private clientId?: string;
  private apiUrl: string;
  private usePKCE: boolean;
  private platformInfo?: PlatformInfo;
  
  /** In-memory cache — avoids re-fetching within the same page session */
  private cachedToken: string | null = null;
  private cachedExpiry: number | null = null;
  private cachedSdkConfig: Record<string, SdkConfigEntry> = {};

  /** Prevents concurrent token refresh calls */
  private pendingTokenPromise: Promise<string> | null = null;

  constructor(opts: PlatformModuleOptions) {
    this.apiUrl = opts.apiUrl;
    this.clientId = opts.clientId;
    this.usePKCE = opts.usePKCE ?? false;

    // Detect or validate platform info for PKCE
    if (this.usePKCE) {
      if (opts.platformType && opts.platformIdentifier) {
        // Explicit platform config (mobile apps)
        validatePlatformIdentifier(opts.platformType, opts.platformIdentifier);
        this.platformInfo = {
          type: opts.platformType,
          identifier: opts.platformIdentifier,
        };
      } else {
        // Auto-detect (web browsers)
        try {
          this.platformInfo = detectPlatform();
        } catch (e: any) {
          throw new Error(
            'PKCE is enabled but platform could not be detected. ' +
            'For mobile apps, provide platformType and platformIdentifier in config. ' +
            `Error: ${e.message}`
          );
        }
      }
    }

    if (opts.initialPlatformToken) {
      this.cachedToken = opts.initialPlatformToken;
      // Treat as no-expiry (trust the caller's server-side logic)
      this.cachedExpiry = Date.now() + 24 * 60 * 60 * 1000;
      storage.set(STORAGE_KEYS.PLATFORM_TOKEN, opts.initialPlatformToken);
    } else {
      // Restore from storage
      const stored = storage.get(STORAGE_KEYS.PLATFORM_TOKEN);
      const storedExpiry = storage.get(STORAGE_KEYS.PLATFORM_TOKEN_EXPIRY);
      if (stored && storedExpiry && Date.now() < parseInt(storedExpiry, 10)) {
        this.cachedToken = stored;
        this.cachedExpiry = parseInt(storedExpiry, 10);
      }
    }
  }

  /**
   * Returns a valid platform token, fetching a new one if needed.
   * Uses a mutex to prevent concurrent refresh calls.
   * Call this before every API request.
   */
  async getToken(): Promise<string> {
    // Return cached token if still valid (5-minute buffer)
    if (this.cachedToken && this.cachedExpiry && Date.now() < this.cachedExpiry - 5 * 60 * 1000) {
      return this.cachedToken;
    }

    // Deduplicate concurrent refresh calls
    if (this.pendingTokenPromise) {
      return this.pendingTokenPromise;
    }

    this.pendingTokenPromise = this.fetchNewToken()
      .then((data) => {
        this.pendingTokenPromise = null;
        return data.platformToken;
      })
      .catch((err) => {
        this.pendingTokenPromise = null;
        throw err;
      });

    return this.pendingTokenPromise;
  }

  /** Current token — may be null if not yet initialised (sync, no refresh) */
  getCurrent(): string | null {
    return this.cachedToken;
  }

  /**
   * Replace the cached platform token at runtime.
   *
   * Use when you receive a fresh pre-generated token (e.g. from a Shopify backend
   * refresh endpoint) and want to apply it without re-creating TryStyleClient.
   *
   * @param token        The new platform token
   * @param expiresInSec Token validity in seconds (default: 24 hours)
   */
  setPlatformToken(token: string, expiresInSec: number = 86400): void {
    this.cachedToken = token;
    this.cachedExpiry = Date.now() + expiresInSec * 1000;
    this.pendingTokenPromise = null; // clear stale refresh attempt so getToken() uses the new value
    storage.set(STORAGE_KEYS.PLATFORM_TOKEN, token);
    storage.set(STORAGE_KEYS.PLATFORM_TOKEN_EXPIRY, String(this.cachedExpiry));
  }

  /**
   * Explicitly fetch a fresh platform token.
   * Chooses flow based on config: PKCE, clientId, or error.
   */
  async fetchNewToken(): Promise<PlatformTokenData> {
    if (this.usePKCE) {
      return this.fetchTokenViaPKCE();
    }

    if (this.clientId) {
      return this.fetchTokenViaClientId();
    }

    throw new TryStyleApiError(
      400,
      'NO_AUTH_METHOD',
      'No authentication method provided. ' +
      'Pass one of: usePKCE: true, clientId, or platformToken in TryStyleConfig.'
    );
  }

  /**
   * PKCE flow: authorize → exchange token
   */
  private async fetchTokenViaPKCE(): Promise<PlatformTokenData> {
    if (!this.platformInfo) {
      throw new Error('Platform info not initialized for PKCE flow');
    }

    // Step 1: Generate PKCE pair
    const { verifier, challenge } = await generatePKCEPair();

    // Step 2: Request authorization code
    const authRes = await fetch(`${this.apiUrl}/api/v1/public/platform/sdk/authorize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        platform_type: this.platformInfo.type,
        platform_identifier: this.platformInfo.identifier,
        code_challenge: challenge,
        code_challenge_method: 'S256',
      }),
    });

    if (!authRes.ok) {
      let msg = `PKCE authorize failed with status ${authRes.status}`;
      try {
        const body = await authRes.json() as { message?: string };
        if (body.message) msg = body.message;
      } catch { /* ignore */ }
      throw new TryStyleApiError(authRes.status, `PKCE_AUTHORIZE_${authRes.status}`, msg);
    }

    const authJson = await authRes.json() as {
      success: boolean;
      data: { authorization_code: string; expires_in: number };
    };

    const authorizationCode = authJson.data.authorization_code;

    // Step 3: Exchange authorization code for platform token
    const tokenRes = await fetch(`${this.apiUrl}/api/v1/public/platform/sdk/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        authorization_code: authorizationCode,
        code_verifier: verifier,
      }),
    });

    if (!tokenRes.ok) {
      let msg = `PKCE token exchange failed with status ${tokenRes.status}`;
      try {
        const body = await tokenRes.json() as { message?: string };
        if (body.message) msg = body.message;
      } catch { /* ignore */ }
      throw new TryStyleApiError(tokenRes.status, `PKCE_TOKEN_${tokenRes.status}`, msg);
    }

    const tokenJson = await tokenRes.json() as {
      success: boolean;
      data: { platform_token: string; expires_at: string; expires_in: number };
    };

    const platformToken = tokenJson.data.platform_token;
    const expiresIn = tokenJson.data.expires_in;

    // Cache token
    this.cachedToken = platformToken;
    this.cachedExpiry = Date.now() + expiresIn * 1000;

    storage.set(STORAGE_KEYS.PLATFORM_TOKEN, platformToken);
    storage.set(STORAGE_KEYS.PLATFORM_TOKEN_EXPIRY, String(this.cachedExpiry));

    return {
      platformToken,
      expiresAt: tokenJson.data.expires_at,
      expiresIn,
    };
  }

  /**
   * Legacy clientId-based flow (origin-validated)
   */
  private async fetchTokenViaClientId(): Promise<PlatformTokenData> {
    if (!this.clientId) {
      throw new TryStyleApiError(400, 'NO_CLIENT_ID', 'clientId is required for browser SDK init');
    }

    const res = await fetch(`${this.apiUrl}/api/v1/public/platform/sdk/init`, {
      method: 'POST',
      headers: {
        'X-Client-Id': this.clientId,
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      let msg = `SDK init failed with status ${res.status}`;
      try {
        const body = await res.json() as { message?: string };
        if (body.message) msg = body.message;
      } catch { /* ignore */ }
      throw new TryStyleApiError(res.status, `SDK_INIT_${res.status}`, msg);
    }

    const json = await res.json() as { success: boolean; data: PlatformTokenData };
    const { platformToken, expiresAt, expiresIn } = json.data;

    this.cachedToken = platformToken;
    this.cachedExpiry = Date.now() + expiresIn * 1000;

    storage.set(STORAGE_KEYS.PLATFORM_TOKEN, platformToken);
    storage.set(STORAGE_KEYS.PLATFORM_TOKEN_EXPIRY, String(this.cachedExpiry));

    return json.data;
  }

  /** Used by the Fetcher to inject the token on each request */
  /**
   * Invalidate the current cached/stored platform token
   * and fetch a fresh one via the configured flow (PKCE / clientId).
   */
  async refreshToken(): Promise<string> {
    // Clear cache + storage
    this.cachedToken = null;
    this.cachedExpiry = null;
    this.cachedSdkConfig = {};
    storage.remove(STORAGE_KEYS.PLATFORM_TOKEN);
    storage.remove(STORAGE_KEYS.PLATFORM_TOKEN_EXPIRY);
    storage.remove(STORAGE_KEYS.PLATFORM_SDK_CONFIG);

    const data = await this.fetchNewToken();
    return data.platformToken;
  }

  /** Returns an async getter that always resolves to a valid token (auto-refreshes if expired) */
  createGetter(): () => Promise<string | null> {
    return async () => {
      try {
        return await this.getToken();
      } catch {
        return null;
      }
    };
  }

  /**
   * Fetch and cache origin/platform-scoped SDK config for this platform token.
   *
   * Backed by: POST /api/v1/public/platform/sdk/config
   * Auth: X-Platform-Token
   *
   * On 401, proactively refreshes the platform token and retries once.
   */
  async getSdkConfig<T = any>(opts: SdkConfigQueryOptions = {}): Promise<T | null> {
    const origin = opts.origin ?? this.detectOrigin();
    const productId = this.normalizeProductId(opts.productId);
    const cacheKey = this.getConfigCacheKey(origin, productId);

    if (!opts.forceRefresh) {
      const cached = this.cachedSdkConfig[cacheKey];
      if (cached && Date.now() < cached.expiresAt) {
        return cached.value as T;
      }

      const stored = this.readStoredConfig(cacheKey);
      if (stored) {
        this.cachedSdkConfig[cacheKey] = stored;
        return stored.value as T;
      }
    }

    for (let attempt = 0; attempt < 2; attempt++) {
      const token = await this.getToken();

      const res = await fetch(`${this.apiUrl}/api/v1/public/platform/sdk/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Platform-Token': token,
        },
        body: JSON.stringify({
          ...(origin ? { origin } : {}),
          ...(productId ? { productId } : {}),
        }),
      });

      if (res.ok) {
        const json = await res.json() as { success?: boolean; data?: { config?: any } };
        const cfg = json?.data?.config ?? null;
        const entry: SdkConfigEntry = { value: cfg, expiresAt: Date.now() + SDK_CONFIG_TTL_MS };
        this.cachedSdkConfig[cacheKey] = entry;
        this.writeStoredConfig(cacheKey, entry);
        return cfg as T;
      }

      // Token rejected — force refresh and retry once
      if (res.status === 401 && attempt === 0) {
        try {
          await this.refreshToken();
          continue;
        } catch {
          return null;
        }
      }

      // Non-auth failure — config is optional, don't hard-fail
      return null;
    }

    return null;
  }

  private normalizeProductId(productId?: string | string[]): string | undefined {
    if (Array.isArray(productId)) {
      const first = productId.find((id) => typeof id === 'string' && id.trim().length > 0);
      return first?.trim();
    }
    if (typeof productId === 'string' && productId.trim().length > 0) {
      return productId.trim();
    }
    return undefined;
  }

  private detectOrigin(): string | undefined {
    if (typeof window === 'undefined' || !window.location?.origin) return undefined;
    return window.location.origin;
  }

  private getConfigCacheKey(origin?: string, productId?: string): string {
    return `${origin ?? 'no-origin'}::${productId ?? 'no-product'}`;
  }

  private readStoredConfig(cacheKey: string): SdkConfigEntry | undefined {
    const raw = storage.get(STORAGE_KEYS.PLATFORM_SDK_CONFIG);
    if (!raw) return undefined;

    try {
      const parsed = JSON.parse(raw) as Record<string, any>;
      if (parsed && typeof parsed === 'object' && cacheKey in parsed) {
        const entry = parsed[cacheKey];
        // Legacy entries were plain config values (no TTL) — treat as stale
        if (
          entry && typeof entry === 'object' &&
          typeof entry.expiresAt === 'number' &&
          Date.now() < entry.expiresAt
        ) {
          return entry;
        }
        delete parsed[cacheKey];
        storage.set(STORAGE_KEYS.PLATFORM_SDK_CONFIG, JSON.stringify(parsed));
      }
      return undefined;
    } catch {
      storage.remove(STORAGE_KEYS.PLATFORM_SDK_CONFIG);
      return undefined;
    }
  }

  private writeStoredConfig(cacheKey: string, entry: SdkConfigEntry): void {
    try {
      const existingRaw = storage.get(STORAGE_KEYS.PLATFORM_SDK_CONFIG);
      const existing = existingRaw ? JSON.parse(existingRaw) as Record<string, any> : {};
      const next = (existing && typeof existing === 'object' ? existing : {}) as Record<string, any>;
      next[cacheKey] = entry;
      storage.set(STORAGE_KEYS.PLATFORM_SDK_CONFIG, JSON.stringify(next));
    } catch {
      /* ignore storage failures */
    }
  }

  /**
   * Clear all cached SDK config (in-memory + localStorage).
   * Next getSdkConfig() call will re-fetch from the backend.
   */
  clearConfigCache(): void {
    this.cachedSdkConfig = {};
    storage.remove(STORAGE_KEYS.PLATFORM_SDK_CONFIG);
  }
}
